import type { z } from 'zod'
import { env } from '@/config/env'
import { randomId } from '@/lib/utils/id'
import { ApiError, codeForStatus, errorEnvelopeSchema } from './errors'

/**
 * Cliente HTTP único do Woctools v2.
 * - base URL de `config/env`
 * - Bearer do OIDC (via `configureHttpClient`, sem acoplar a lib de auth)
 * - `X-Request-ID` por requisição (devolvido em `ApiError.requestId`)
 * - timeout, normalização do envelope de erro e 401 → reautenticação
 * Componentes nunca chamam `fetch`: usam hooks de feature (TanStack Query) sobre `request`.
 */

export type QueryParams = Record<string, string | number | boolean | string[] | null | undefined>

export interface HttpClientConfig {
  getAccessToken: () => string | null | undefined
  onUnauthorized: () => void
}

let config: HttpClientConfig = {
  getAccessToken: () => null,
  onUnauthorized: () => undefined,
}

export function configureHttpClient(next: Partial<HttpClientConfig>): void {
  config = { ...config, ...next }
}

export interface RequestOptions<T> {
  schema: z.ZodType<T, z.ZodTypeDef, unknown>
  method?: 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE'
  query?: QueryParams
  body?: unknown
  signal?: AbortSignal
  timeoutMs?: number
  /** Sobrescreve o X-Request-ID gerado (útil para correlação explícita). */
  requestId?: string
}

export function buildUrl(path: string, query?: QueryParams): string {
  const base = env.apiBaseUrl || window.location.origin
  const url = new URL(`${base}${path.startsWith('/') ? path : `/${path}`}`)
  if (query) {
    for (const [key, value] of Object.entries(query)) {
      if (value === null || value === undefined || value === '') continue
      if (Array.isArray(value)) {
        for (const item of value) url.searchParams.append(key, item)
      } else {
        url.searchParams.set(key, String(value))
      }
    }
  }
  return url.toString()
}

async function readErrorPayload(response: Response): Promise<unknown> {
  try {
    return await response.json()
  } catch {
    return null
  }
}

export async function request<T>(path: string, options: RequestOptions<T>): Promise<T> {
  const { schema, method = 'GET', query, body, signal } = options
  const requestId = options.requestId ?? randomId()
  const controller = new AbortController()
  let timedOut = false
  const timeoutId = setTimeout(() => {
    timedOut = true
    controller.abort()
  }, options.timeoutMs ?? env.apiTimeoutMs)
  const onExternalAbort = () => controller.abort()
  if (signal?.aborted) controller.abort()
  signal?.addEventListener('abort', onExternalAbort)

  const headers: Record<string, string> = {
    Accept: 'application/json',
    'X-Request-ID': requestId,
  }
  if (body !== undefined) headers['Content-Type'] = 'application/json'
  const token = config.getAccessToken()
  if (token) headers.Authorization = `Bearer ${token}`

  let response: Response
  try {
    response = await fetch(buildUrl(path, query), {
      method,
      headers,
      body: body !== undefined ? JSON.stringify(body) : undefined,
      signal: controller.signal,
    })
  } catch (error) {
    if (timedOut) {
      throw new ApiError('TIMEOUT', 'O servidor demorou mais do que o esperado para responder.', {
        requestId,
      })
    }
    // Cancelamento pedido pelo chamador (ex.: TanStack Query desmontou): repassa como está.
    if (signal?.aborted) throw error
    throw new ApiError('NETWORK_ERROR', 'Não foi possível conectar ao servidor.', {
      requestId,
      details: error instanceof Error ? error.message : undefined,
    })
  } finally {
    clearTimeout(timeoutId)
    signal?.removeEventListener('abort', onExternalAbort)
  }

  const responseRequestId = response.headers.get('x-request-id') ?? requestId

  if (!response.ok) {
    const payload = await readErrorPayload(response)
    const envelope = errorEnvelopeSchema.safeParse(payload)
    const error = envelope.success
      ? new ApiError(envelope.data.error.code, envelope.data.error.message, {
          status: response.status,
          requestId: envelope.data.error.request_id ?? responseRequestId,
          details: envelope.data.error.details,
        })
      : new ApiError(
          codeForStatus(response.status),
          `O servidor respondeu com erro ${response.status}.`,
          { status: response.status, requestId: responseRequestId },
        )
    if (response.status === 401) config.onUnauthorized()
    throw error
  }

  if (response.status === 204) return schema.parse(undefined)

  let json: unknown
  try {
    json = await response.json()
  } catch {
    throw new ApiError('INVALID_RESPONSE', 'O servidor devolveu uma resposta que não é JSON.', {
      status: response.status,
      requestId: responseRequestId,
    })
  }

  const parsed = schema.safeParse(json)
  if (!parsed.success) {
    throw new ApiError('INVALID_RESPONSE', 'A resposta do servidor não está no formato esperado.', {
      status: response.status,
      requestId: responseRequestId,
      details: parsed.error.flatten(),
    })
  }
  return parsed.data
}
